import React, { useRef, useState, useEffect } from 'react'
import { useNavigate } from "react-router-dom"
import { UpdateTranscriptVisualState, exportDocx, saveJson, customReplace, search, clearSearch } from './functions'
import {
    Sidebar,
    SidebarContent,
    SidebarGroup,
    SidebarGroupContent,
    SidebarSeparator,
    Button,
    Input,
    Tooltip,
    TooltipTrigger,
    TooltipContent,
    Slider,
    Switch
} from "../shadcnComponents/ui"
import { Home, ArrowLeftIcon, SearchIcon, ReplaceAllIcon } from "lucide-react"

export function SideBar(props) {
    const navigate = useNavigate()
    const [playbackSpeed, setPlaybackSpeed] = useState([1]);
    const [displaySPeakers, setDisplaySpeakers] = useState(true);
    const [autoScrolling, setAutoScrolling] = useState(false);
    const [isPLaying, setPLaying] = useState(false);
    const audioRef = useRef(null);

    // Update playback rate
    useEffect(() => {
        document.getElementById('audio-player').playbackRate = playbackSpeed[0];
    }, [playbackSpeed]);

    useEffect(() => {
        if (document.getElementsByClassName('active')) {
            let scrollInterval = null;
            if (autoScrolling) {
                scrollInterval = setInterval(() => {
                    document.getElementsByClassName('active')[0] ?
                        document.getElementsByClassName('active')[0].scrollIntoView({ block: 'center', behavior: 'smooth' })
                        :
                        () => { };
                }, 1000);
            }
            return (() => { clearInterval(scrollInterval); });
        }
    }, [autoScrolling]);

    // Update the transcript's visual state on play
    useEffect(() => {
        if (document.getElementById('hypertranscript').firstChild) {
            let interval = null;
            if (isPLaying) {
                interval = setInterval(() => UpdateTranscriptVisualState(document.getElementById('audio-player').currentTime), 100);
            }
            return (() => { clearInterval(interval); });
        }
    }, [isPLaying]);


    // Toggle visibility of speaker names
    useEffect(() => {
        const speakers = Array.from(document.getElementsByClassName('speaker'));
        if (displaySPeakers == true) {
            speakers.forEach((speaker) => {
                speaker.removeAttribute('style');
            });
        } else {
            speakers.forEach((speaker) => {
                speaker.style.display = 'none';
            });
        }
    }, [displaySPeakers]);

    function replace() {
        if (document.getElementById('replace-box') && document.getElementById('replace-box').value) {
            const { value } = document.getElementById('replace-box');
            let wordsToReplace = search()
            wordsToReplace.map((each) => {
                each.innerHTML = customReplace(each.innerHTML, document.getElementById('search-box').value, value);
            });
        }
    }

    return (
        <Sidebar width="24rem">
            <SidebarContent>
                {/* Navigation */}
                <div className='p-3 flex flex-row items-center'>
                    <Button variant="ghost" size="icon" onClick={() => { history.back() }}>
                        <ArrowLeftIcon className='size-6' />
                    </Button>
                    <Button variant="ghost" size="icon" className='ml-auto' onClick={() => { navigate('/home') }}>
                        <Home className='size-6' />
                    </Button>
                </div>
                <div id="title" className="cursor-default text-lg font-semibold overflow-hidden px-7 break-all">{props.title != null ? props.title : "Δεν έχει φορτωθεί κάποια συνέντευξη"}</div>
                <SidebarGroup>
                    <SidebarGroupContent>
                        {/* Search and Replace */}
                        <div className='flex flex-row items-center justify-center pl-5 my-5 gap-2'>
                            <Input type="search" id="search-box" placeholder='Αναζήτηση' onInput={e => { if (e.target.value == "") { e.preventDefault(); clearSearch() } }} onKeyDown={(event) => { event.key === 'Enter' ? search() : () => { }; }} ></Input>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button variant='ghost' size='icon' className="mr-1" onClick={() => { search() }}>
                                        <SearchIcon className='size-5' />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side={'right'}>Αναζήτηση</TooltipContent>
                            </Tooltip>
                        </div>
                        <div className='flex flex-row items-center justify-center pl-5 my-5 gap-2'>
                            <Input type="search" id="replace-box" placeholder='Αντικατάσταση' onKeyDown={(event) => { event.key === 'Enter' ? replace() : () => { }; }}></Input>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button variant='ghost' size='icon' className="mr-1" onClick={() => { replace() }}>
                                        <ReplaceAllIcon className='size-5' />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent side={'right'}>{"Αντικατάσταση όλων"}</TooltipContent>
                            </Tooltip>
                        </div>
                        {/* Toggle speakers and auto scrolling */}
                        <div className="mt-9 w-full flex flex-row justify-end items-center gap-4 pr-2">
                            <label className="flex items-start justify-end" htmlFor="show-speakers">Προβολή ομιλητών</label>
                            <Switch onCheckedChange={(e) => { setDisplaySpeakers(e); }} checked={displaySPeakers} />
                        </div>
                        <div className="mt-4 w-full flex flex-row justify-end items-center gap-4 pr-2">
                            <label className="flex items-start justify-end" htmlFor="show-speakers">Αυτόματη κύληση</label>
                            <Switch onCheckedChange={(e) => { setAutoScrolling(e); }} checked={autoScrolling} />
                        </div>
                    </SidebarGroupContent>
                </SidebarGroup>
                <SidebarSeparator className='my-8 w-full' />
                <SidebarGroup>
                    <SidebarGroupContent>
                        <audio ref={audioRef} onPlaying={() => setPLaying(true)} onPause={() => { setPLaying(false); }} id="audio-player" className="w-full rounded-xl" type="audio/mp3" controls controlsList="nodownload noplaybackrate" preload="auto">
                            Your browser does not support the audio player
                        </audio>
                        <div className="flex flex-col items-start justify-center pt-5 px-6">
                            <label className='text-xs mb-1'>Ταχύτητα αναπαραγωγής</label>
                            <div className='flex flex-row items-center justify-center w-full gap-2'>
                                <Slider className='w-full' onValueChange={(e) => { setPlaybackSpeed(e); }} defaultValue={[1]} max={2.4} step={0.2} />
                                <label>{playbackSpeed[0]}</label>
                            </div>
                        </div>
                    </SidebarGroupContent>
                </SidebarGroup>
                <SidebarSeparator className='mt-auto mb-6 w-full' />
                <SidebarGroup className='h-fit pb-8'>
                    <SidebarGroupContent className='flex flex-col h-fit gap-5 px-5'>
                        <Button variant="secondary" size="lg" className='w-full' onClick={() => { saveJson(props.interviewId, props.unsetChangeListener) }}>
                            Αποθήκευση
                        </Button>
                        <Button variant="secondary" size="lg" className='w-full' onClick={() => { exportDocx() }}>
                            Εξαγωγή αρχείου κειμένου
                        </Button>
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>
        </Sidebar>
    )
}

export default SideBar;